import { buildTreeWithMeta } from "./orgTreeUtils";
import { migrateOrgFlat, normalizeCustomerRecord, emptyPortalAdmin } from "./customerModel";

/**
 * Merge one organization row (from GET-by-id) into the flat list by `id`.
 * @param {import('./orgTreeUtils').OrgFlatNode[]} prev
 * @param {import('./orgTreeUtils').OrgFlatNode} row
 */
export function mergeOrgRowIntoFlat(prev, row) {
  if (!row?.id) return prev;
  const idx = prev.findIndex((o) => o.id === row.id);
  if (idx === -1) return [...prev, row];
  return prev.map((o, i) => (i === idx ? { ...o, ...row } : o));
}

/**
 * POST get-by-id response → single flat org row (with `customers[]` when present).
 * @param {unknown} raw
 */
export function normalizeGetOrganizationResponse(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = /** @type {Record<string, unknown>} */ (raw);
  const orgPayload = r.organization ?? r.org ?? r;
  const flatOrg = apiOrganizationToFlatRow(
    typeof orgPayload === "object" && orgPayload !== null ? /** @type {Record<string, unknown>} */ (orgPayload) : {}
  );
  const customersRaw = r.customers ?? r.customerList ?? (orgPayload && /** @type {Record<string, unknown>} */ (orgPayload).customers);
  if (Array.isArray(customersRaw) && customersRaw.length) {
    flatOrg.customers = customersRaw.map((c) => normalizeCustomerRecord(c, flatOrg.id));
  }
  return migrateOrgFlat([flatOrg])[0];
}

/**
 * POST customers/list response → customer[] for one org.
 * @param {unknown} raw
 * @param {string} organizationId
 */
export function normalizeCustomerListResponse(raw, organizationId) {
  const r = raw && typeof raw === "object" ? /** @type {Record<string, unknown>} */ (raw) : {};
  const list =
    (Array.isArray(raw) && raw) ||
    r.records ||
    r.items ||
    r.customers ||
    r.data ||
    r.content ||
    [];
  if (!Array.isArray(list)) return [];
  return list.map((c) => normalizeCustomerRecord(c, organizationId));
}

/**
 * POST check-code response → boolean (true = code can be used).
 * @param {unknown} res
 */
export function pickCodeAvailableResponse(res) {
  if (typeof res === "boolean") return res;
  if (res == null) return true;
  if (typeof res === "object" && "available" in res) {
    return Boolean(/** @type {Record<string, unknown>} */ (res).available);
  }
  const r = /** @type {Record<string, unknown>} */ (res);
  if (r.data && typeof r.data === "object" && "available" in /** @type {Record<string, unknown>} */ (r.data)) {
    return Boolean(/** @type {Record<string, unknown>} */ (r.data).available);
  }
  if (typeof r.isAvailable === "boolean") return r.isAvailable;
  return true;
}

/**
 * @typedef {Object} OrganizationCreateRequest
 * @property {string} tenantId
 * @property {string} organizationCode
 * @property {string} organizationName
 * @property {string} [organizationShortName]
 * @property {string} organizationType
 * @property {string|null} [parentOrganizationId]
 * @property {boolean} [legalEntityFlag]
 * @property {boolean} [operationalEntityFlag]
 * @property {string} [defaultCurrency]
 * @property {string} [defaultCountry]
 * @property {string} [timeZone]
 * @property {string} [primaryContactName]
 * @property {string} [primaryContactEmail]
 * @property {string} [primaryContactMobile]
 * @property {{ contactName?: string, contactEmail?: string, contactMobile?: string, contactRole?: string }[]} [additionalContacts]
 * @property {string} [addressLine1]
 * @property {string} [addressLine2]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [country]
 * @property {string} [postalCode]
 * @property {string} [status]
 * @property {string} [effectiveFrom]
 * @property {string} [effectiveTo]
 * @property {string[]} [mappedUserIds]
 * @property {string[]} [mappedCustomerIds]
 * @property {string} [costCenter]
 * @property {string} [businessUnit]
 * @property {string} [departmentType]
 * @property {string} [gstin]
 * @property {string} [pan]
 * @property {string} [portalRoleId] Mongo `_id` of role from `auth/roles/fetch`
 * @property {{ username?: string, password?: string, confirmPassword?: string, firstName?: string, lastName?: string, mobile?: string, countryCode?: string }} [portalAdmin]
 */

/**
 * Map UI flat row (OrganizationDetailsTab / tree) → POST `tenant/organization/create` body.
 * @param {import('./orgTreeUtils').OrgFlatNode} row
 * @param {{ tenantId?: string }} ctx
 * @returns {OrganizationCreateRequest}
 */
export function flatRowToOrganizationCreateRequest(row, ctx = {}) {
  const tenantId = ctx.tenantId ?? row.tenantId ?? "";
  const contacts = (row.contacts || []).map((c) => ({
    contactName: c.name ?? "",
    contactEmail: c.email ?? "",
    contactMobile: c.mobile ?? "",
    contactRole: c.role ?? "",
  }));
  const portalAdminPayload = buildPortalAdminForApi(row.portalAdmin);
  return {
    tenantId,
    organizationCode: row.code,
    organizationName: row.name,
    organizationShortName: row.shortName ?? "",
    organizationType: row.type,
    parentOrganizationId: row.parentId ?? null,
    legalEntityFlag: !!row.legalEntity,
    operationalEntityFlag: row.operationalEntity !== false,
    defaultCurrency: row.currency ?? "INR",
    defaultCountry: row.country ?? "IN",
    timeZone: row.timeZone ?? "Asia/Kolkata",
    primaryContactName: row.primaryContact ?? "",
    primaryContactEmail: row.primaryEmail ?? "",
    primaryContactMobile: row.primaryMobile ?? "",
    additionalContacts: contacts,
    addressLine1: row.address1 ?? "",
    addressLine2: row.address2 ?? "",
    city: row.city ?? "",
    state: row.state ?? "",
    country: row.country ?? "",
    postalCode: row.postalCode ?? "",
    status: row.status ?? "Active",
    effectiveFrom: row.effectiveFrom ?? "",
    effectiveTo: row.effectiveTo ?? "",
    mappedUserIds: row.mappedUserIds ?? [],
    mappedCustomerIds: row.mappedCustomerIds ?? [],
    costCenter: row.costCenter ?? "",
    businessUnit: row.businessUnit ?? "",
    departmentType: row.departmentType ?? "",
    gstin: row.gstin ?? "",
    pan: row.pan ?? "",
    ...(row.portalRoleId ? { portalRoleId: String(row.portalRoleId) } : {}),
    ...(portalAdminPayload ? { portalAdmin: portalAdminPayload } : {}),
  };
}

/** Shape for POST create/update organization when portal admin is used. */
function buildPortalAdminForApi(pa) {
  if (!pa || typeof pa !== "object") return null;
  const out = { ...emptyPortalAdmin(), ...pa };
  if (!out.password?.trim()) {
    delete out.password;
    delete out.confirmPassword;
  }
  const has =
    out.username?.trim() ||
    out.firstName?.trim() ||
    out.lastName?.trim() ||
    out.mobile?.trim() ||
    out.password?.trim();
  return has ? out : null;
}

/**
 * Same as create; used for update (server may ignore tenantId if immutable).
 */
export function flatRowToOrganizationUpdateRequest(row, ctx = {}) {
  return {
    ...flatRowToOrganizationCreateRequest(row, ctx),
    organizationId: row.id,
  };
}

/** Backend often sends SCREAMING_SNAKE / UPPERCASE; UI uses `ORG_STATUS` title-case values. */
export function normalizeOrganizationStatusFromApi(status) {
  if (status == null || status === "") return "Active";
  const key = String(status).trim().toUpperCase();
  const map = {
    ACTIVE: "Active",
    INACTIVE: "Inactive",
    BLOCKED: "Blocked",
    ARCHIVED: "Archived",
  };
  if (map[key]) return map[key];
  const lower = String(status).trim().toLowerCase();
  for (const label of ["Active", "Inactive", "Blocked", "Archived"]) {
    if (label.toLowerCase() === lower) return label;
  }
  return String(status);
}

/**
 * Map API organization DTO → UI flat node (best-effort; supports snake_case or camelCase).
 * @param {Record<string, unknown>} raw
 */
export function apiOrganizationToFlatRow(raw) {
  const g = (a, b) => (raw[a] != null ? raw[a] : raw[b]);
  const id = String(g("organizationId", "id") ?? raw._id ?? "");
  const parentId = g("parentOrganizationId", "parentId");
  const contactsRaw = g("additionalContacts", "contacts") ?? [];
  const contacts = Array.isArray(contactsRaw)
    ? contactsRaw.map((c, i) => ({
        id: String(c.id ?? c.contactId ?? `ct-${id}-${i}`),
        name: c.contactName ?? c.name ?? "",
        email: c.contactEmail ?? c.email ?? "",
        mobile: c.contactMobile ?? c.mobile ?? "",
        role: c.contactRole ?? c.role ?? "",
      }))
    : [];

  const row = {
    id,
    code: String(g("organizationCode", "code") ?? ""),
    name: String(g("organizationName", "name") ?? ""),
    shortName: g("organizationShortName", "shortName") ?? "",
    type: String(g("organizationType", "type") ?? "Company"),
    parentId: parentId == null || parentId === "" ? null : String(parentId),
    status: normalizeOrganizationStatusFromApi(g("status", "status")),
    hierarchyLevel: g("hierarchyLevel", "hierarchyLevel"),
    hierarchyPath: g("hierarchyPath", "hierarchyPath") ?? "",
    tenantId: g("tenantId", "tenantId") ?? "",
    legalEntity: Boolean(g("legalEntityFlag", "legalEntity")),
    operationalEntity: g("operationalEntityFlag", "operationalEntity") !== false,
    currency: g("defaultCurrency", "currency") ?? "INR",
    country: g("defaultCountry", "country") ?? "IN",
    state: g("state", "state") ?? "",
    city: g("city", "city") ?? "",
    address1: g("addressLine1", "address1") ?? "",
    address2: g("addressLine2", "address2") ?? "",
    postalCode: g("postalCode", "postalCode") ?? "",
    timeZone: g("timeZone", "timeZone") ?? "Asia/Kolkata",
    primaryContact: g("primaryContactName", "primaryContact") ?? "",
    primaryEmail: g("primaryContactEmail", "primaryEmail") ?? "",
    primaryMobile: g("primaryContactMobile", "primaryMobile") ?? "",
    contacts,
    effectiveFrom: g("effectiveFrom", "effectiveFrom") ?? "",
    effectiveTo: g("effectiveTo", "effectiveTo") ?? "",
    mappedUserIds: g("mappedUserIds", "mappedUserIds") ?? [],
    mappedCustomerIds: g("mappedCustomerIds", "mappedCustomerIds") ?? [],
    costCenter: g("costCenter", "costCenter") ?? "",
    businessUnit: g("businessUnit", "businessUnit") ?? "",
    departmentType: g("departmentType", "departmentType") ?? "",
    gstin: g("gstin", "gstin") ?? "",
    pan: g("pan", "pan") ?? "",
    createdBy: g("createdBy", "createdBy") ?? "",
    createdOn: g("createdOn", "createdOn") ?? "",
    updatedBy: g("updatedBy", "updatedBy") ?? "",
    updatedOn: g("updatedOn", "updatedOn") ?? "",
    portalRoleId: g("portalRoleId", "portalRoleId") ? String(g("portalRoleId", "portalRoleId")) : "",
    portalAdmin: { ...emptyPortalAdmin(), ...(typeof raw.portalAdmin === "object" ? raw.portalAdmin : {}) },
    customers: [],
  };

  const cust = g("customers", "customerList");
  if (Array.isArray(cust) && cust.length) {
    row.customers = cust.map((c) => normalizeCustomerRecord(c, row.id));
  }
  return row;
}

/**
 * Normalize various API tree/list responses to OrgFlatNode[].
 * @param {unknown} apiPayload
 * @returns {import('./orgTreeUtils').OrgFlatNode[]}
 */
export function normalizeOrganizationTreeResponse(apiPayload) {
  if (!apiPayload) return [];
  if (Array.isArray(apiPayload)) {
    return migrateOrgFlat(apiPayload.map((x) => apiOrganizationToFlatRow(x)));
  }
  const p = apiPayload;
  const list =
    p.organizations ||
    p.organizationList ||
    p.nodes ||
    p.data ||
    p.records ||
    (Array.isArray(p) ? p : null);
  if (!Array.isArray(list)) return [];
  return migrateOrgFlat(list.map((x) => apiOrganizationToFlatRow(x)));
}

/**
 * After local or remote flat list change, ensure hierarchy fields exist on every row.
 * @param {import('./orgTreeUtils').OrgFlatNode[]} flat
 */
export function attachHierarchyMeta(flat) {
  const { levels, paths } = buildTreeWithMeta(flat);
  return flat.map((r) => ({
    ...r,
    hierarchyLevel: levels[r.id],
    hierarchyPath: paths[r.id],
  }));
}

/**
 * Extract new organization id from create API response.
 * @param {unknown} res
 */
export function pickOrganizationIdFromCreateResponse(res) {
  if (!res || typeof res !== "object") return null;
  const r = /** @type {Record<string, unknown>} */ (res);
  return (
    (r.organizationId && String(r.organizationId)) ||
    (r.id && String(r.id)) ||
    (r.data && typeof r.data === "object" && r.data.organizationId && String(r.data.organizationId)) ||
    null
  );
}

/**
 * Customer onboarding form payload → API body (adds organizationId for org-scoped routes).
 * @param {string} organizationId
 * @param {Record<string, unknown>} customer
 */
export function customerFormToOrgApiPayload(organizationId, customer) {
  return {
    ...customer,
    organizationId,
    orgMappingId: organizationId,
  };
}
